/**
 * ReviewQueue
 *
 * The keep/skip review queue, extracted out of ShiftBar (docs/design/
 * STEAL-SPEC.md — "extract the queue into a shared component... behavior
 * unchanged"). Renders whatever is in review-queue-store: each plan step
 * shows the move, the target chord, and a handwriting-voice rationale with
 * keep / skip. Keeping a step runs it through the SAME apply flow as the
 * moves palette (via onApplyStep). A step whose move no longer fits the
 * current progression (an earlier keep changed the context) is skipped
 * kindly rather than erroring.
 *
 * Two producers feed this ONE instance: ShiftBar's /api/shift path, and
 * Steal from Masters' planFromInfluence ("voice it like {name}"). Neither
 * renders its own copy — they both call the store's loadPlan/loadError.
 */
import React from 'react';
import { MOVES } from '@/lib/moves';
import { useReviewQueueStore } from '@/store/review-queue-store';
import styles from './ReviewQueue.module.css';

interface ReviewQueueProps {
  /** Apply a single kept step through the shared moves flow. */
  onApplyStep: (moveId: string, index: number) => 'applied' | 'not-applicable';
}

function moveName(moveId: string): string {
  return MOVES.find((m) => m.id === moveId)?.name ?? moveId;
}

const ReviewQueueComponent: React.FC<ReviewQueueProps> = ({ onApplyStep }) => {
  const error = useReviewQueueStore((s) => s.error);
  const summary = useReviewQueueStore((s) => s.summary);
  const steps = useReviewQueueStore((s) => s.steps);
  const setStepStatus = useReviewQueueStore((s) => s.setStepStatus);
  const dismiss = useReviewQueueStore((s) => s.dismiss);

  const hasQueue = steps !== null || error !== null;
  if (!hasQueue) return null;

  const keepStep = (index: number) => {
    if (!steps) return;
    const step = steps[index];
    const outcome = onApplyStep(step.moveId, step.index);
    setStepStatus(index, outcome === 'applied' ? 'kept' : 'not-applicable');
  };

  const skipStep = (index: number) => setStepStatus(index, 'skipped');

  return (
    <div className={styles.queue}>
      <button type="button" className={styles.dismiss} onClick={dismiss} aria-label="Dismiss">
        &times;
      </button>

      {error && <p className={styles.errorLine}>{error}</p>}

      {steps && summary && <p className={styles.summary}>{summary}</p>}

      {steps && steps.length > 0 && (
        <ul className={styles.stepList}>
          {steps.map((step, i) => (
            <li key={`${step.moveId}-${step.index}-${i}`} className={styles.step}>
              <div className={styles.stepHead}>
                <span className={styles.stepMove}>{moveName(step.moveId)}</span>
                <span className={styles.stepTarget}>chord {step.index + 1}</span>
              </div>
              <p className={styles.stepRationale}>{step.rationale}</p>
              {step.status === 'pending' ? (
                <div className={styles.stepActions}>
                  <button type="button" className={styles.keep} onClick={() => keepStep(i)}>
                    keep
                  </button>
                  <button type="button" className={styles.skip} onClick={() => skipStep(i)}>
                    skip
                  </button>
                </div>
              ) : (
                <p className={styles.stepStatus}>
                  {step.status === 'kept' && 'kept'}
                  {step.status === 'skipped' && 'skipped'}
                  {step.status === 'not-applicable' && 'no longer fits — skipped'}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

ReviewQueueComponent.displayName = 'ReviewQueue';

export const ReviewQueue = React.memo(ReviewQueueComponent);
